"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { PrimaryButton } from "../buttons/PrimaryButton";
import { navigation, NavigationItem } from "@/data/navigation";
import logo from "@/assets/images/logos/logo.svg";

import { motion, AnimatePresence } from "framer-motion";

interface HeaderProps {
  className?: string;
}

export function Header({ className = "" }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const [isCompact, setIsCompact] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  const toggleMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setMobileDropdownOpen(null);
  };

  const toggleMobileDropdown = (itemName: string) => {
    setMobileDropdownOpen((prev) => (prev === itemName ? null : itemName));
  };

  // Check if we're on the home page
  const isHomePage = pathname === "/";

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Compact header whenever not at top; expand only when back at the top
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      const current = window.scrollY || 0;
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsCompact(current > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    // Initialize on mount
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Track viewport to disable animations on mobile/tablet (< lg)
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsDesktop("matches" in e ? e.matches : (e as MediaQueryList).matches);
    };
    onChange(mq as unknown as MediaQueryList);
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener(
        "change",
        onChange as (this: MediaQueryList, ev: MediaQueryListEvent) => any
      );
    } else {
      // @ts-ignore - Safari
      mq.addListener(onChange);
    }
    return () => {
      if (typeof mq.removeEventListener === "function") {
        mq.removeEventListener(
          "change",
          onChange as (this: MediaQueryList, ev: MediaQueryListEvent) => any
        );
      } else {
        // @ts-ignore - Safari
        mq.removeListener(onChange);
      }
    };
  }, []);

  if (!mounted) {
    return null;
  }

  const menuVariants = {
    closed: {
      opacity: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
    open: {
      opacity: 1,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const contentVariants = {
    closed: { y: 20, opacity: 0 },
    open: {
      y: 0,
      opacity: 1,
      transition: {
        delay: 0.3,
        duration: 1.2,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const mobileMenuListVariants = {
    open: {
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.5,
      },
    },
    closed: {},
  };
  const mobileMenuItemVariants = {
    closed: { y: 10, opacity: 0 },
    open: {
      y: 0,
      opacity: 1,
      transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] },
    },
  };

  // Delay CTA so it appears slightly before the last nav item fully completes
  const mobileCtaDelay =
    0.5 + 0.15 * Math.max(navigation.length - 1, 0) + 1.2 - 0.2;

  // Build desktop groups based on per-item desktopPosition flag
  const leftNavItems = navigation.filter(
    (item: NavigationItem) => item.desktopPosition !== "right"
  );
  const rightNavItems = navigation.filter(
    (item: NavigationItem) => item.desktopPosition === "right"
  );

  return (
    <>
      {(() => {
        // Determine icon color for mobile burger: dark on light backgrounds
        return null;
      })()}
      <header
        role="banner"
        className={`fixed left-0 right-0 top-0 z-40 flex w-full items-center ${!isDesktop && (isCompact || mobileMenuOpen) ? "bg-primary" : "bg-transparent"} transition-colors duration-300 px-6 md:px-8 lg:px-16 py-[clamp(12px,2vh,28px)] ${className}`}
        style={{
          backfaceVisibility: "hidden",
        }}
      >
        <div className="mx-auto grid w-full max-w-7xl grid-cols-[1fr_auto_1fr] items-center gap-12 xl:gap-48">
          {/* Navigation gauche */}
          <motion.div
            className="hidden items-center space-x-8 justify-end col-start-1 lg:flex"
            animate={{ y: isCompact ? -24 : 0, opacity: isCompact ? 0 : 1 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {leftNavItems.map((item: NavigationItem, index: number) => (
              <div key={item.name} className="relative">
                {item.children && item.children.length > 0 ? (
                  <div className="group">
                    <Link
                      href={item.href}
                      className="flex items-center gap-1 text-xs xl:text-sm uppercase tracking-wide transition-colors duration-300 hover:text-secondary/75"
                    >
                      {item.name}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="size-3 xl:size-4 text-current transition-transform duration-300 group-hover:rotate-180"
                        aria-hidden
                      >
                        <path d="M12 15.5a1 1 0 0 1-.7-.29l-6-6a1 1 0 1 1 1.4-1.42L12 13.09l5.3-5.3a1 1 0 1 1 1.4 1.42l-6 6a1 1 0 0 1-.7.29z" />
                      </svg>
                    </Link>
                    <div className="invisible absolute left-0 top-full z-50 mt-3 w-56 bg-primary text-accent shadow-xl ring-1 ring-accent/10 opacity-0 translate-y-2 transition-all duration-300 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0">
                      <ul className="py-2">
                        {item.children.map((child) => (
                          <li key={child.name}>
                            <Link
                              href={child.href}
                              className="block px-4 py-2 text-sm tracking-wide transition-colors duration-200 hover:text-tertiary"
                            >
                              {child.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <Link
                    href={item.href}
                    className="group relative text-xs xl:text-sm uppercase tracking-wide transition-colors duration-300 hover:text-secondary/75"
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </motion.div>

          {/* Logo : centré sur desktop / petit à gauche quand menu mobile ouvert */}
          <motion.div
            className={`flex items-center justify-center lg:col-start-2 lg:justify-self-center ${mobileMenuOpen ? "col-start-1 justify-self-start" : "col-start-2 justify-self-center"
              }`}
            animate={
              isDesktop
                ? { scale: isCompact ? 0.6 : 1, y: isCompact ? -20 : 0 }
                : { scale: 1, y: 0 }
            }
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link href="/" className="z-50" onClick={mobileMenuOpen ? toggleMenu : undefined}>
              <Image
                src={logo}
                alt="Logo"
                width={144}
                height={144}
                className="hidden lg:block"
              />
              <Image
                src={logo}
                alt="Logo"
                width={96}
                height={96}
                className={mobileMenuOpen ? "hidden" : "block lg:hidden"}
              />
              <Image
                src={logo}
                alt="Logo"
                width={48}
                height={48}
                className={mobileMenuOpen ? "block lg:hidden" : "hidden"}
              />
            </Link>
          </motion.div>

          {/* Navigation droite */}
          <motion.div
            className="hidden items-center space-x-8 justify-start col-start-3 lg:flex"
            animate={{ y: isCompact ? -24 : 0, opacity: isCompact ? 0 : 1 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {rightNavItems.map((item: NavigationItem, index: number) => (
              <div key={item.name}>
                <Link
                  href={item.href}
                  className="group relative text-xs xl:text-sm uppercase tracking-wide transition-colors duration-300 hover:text-secondary/75"
                >
                  {item.name}
                </Link>
              </div>
            ))}
            <PrimaryButton variant="tenup" />
          </motion.div>

          {/* Bouton menu mobile */}
          <button
            onClick={toggleMenu}
            className="z-50 col-start-3 justify-self-end lg:hidden"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`size-[clamp(24px,2.5vh,32px)] ${(!isDesktop && isCompact) || mobileMenuOpen ? "text-accent" : "text-primary"}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            ) : (
              <div className="flex flex-col gap-1.5">
                <span
                  className={`block h-px w-[clamp(24px,2.5vh,32px)] transition-all duration-300 ${(!isDesktop && isCompact) || mobileMenuOpen ? "bg-accent" : "bg-primary"}`}
                />
                <span
                  className={`block h-px w-[clamp(24px,2.5vh,32px)] transition-all duration-300 ${(!isDesktop && isCompact) || mobileMenuOpen ? "bg-accent" : "bg-primary"}`}
                />
                <span
                  className={`block h-px w-[clamp(24px,2.5vh,32px)] transition-all duration-300 ${(!isDesktop && isCompact) || mobileMenuOpen ? "bg-accent" : "bg-primary"}`}
                />
              </div>
            )}
          </button>
        </div>
      </header>

      {/* Menu mobile overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-30 bg-primary lg:hidden"
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            onTouchMove={(e) => e.preventDefault()}
            style={{ touchAction: "none" }}
          >
            <motion.div
              className="flex h-full flex-col items-center justify-start overflow-y-auto overflow-x-hidden px-4 pt-32 pb-8"
              variants={contentVariants}
            >
              <motion.div
                className="flex w-full flex-col items-center space-y-6 text-center"
                variants={mobileMenuListVariants}
                initial="closed"
                animate="open"
              >
                {navigation.map((item: NavigationItem, index: number) => (
                  <motion.div
                    key={item.name}
                    variants={mobileMenuItemVariants}
                    className="w-full max-w-md"
                  >
                    {item.children && item.children.length > 0 ? (
                      <div className="flex flex-col items-center">
                        <button
                          type="button"
                          onClick={() => toggleMobileDropdown(item.name)}
                          className="flex w-full items-center justify-center gap-1.5 text-base font-light uppercase tracking-wide transition-colors duration-300 text-accent hover:text-accent/80"
                          aria-expanded={mobileDropdownOpen === item.name}
                          aria-haspopup="true"
                        >
                          {item.name}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className={`size-4 transition-transform duration-300 ${mobileDropdownOpen === item.name ? "rotate-180" : ""}`}
                            aria-hidden
                          >
                            <path d="M12 15.5a1 1 0 0 1-.7-.29l-6-6a1 1 0 1 1 1.4-1.42L12 13.09l5.3-5.3a1 1 0 1 1 1.4 1.42l-6 6a1 1 0 0 1-.7.29z" />
                          </svg>
                        </button>
                        <AnimatePresence>
                          {mobileDropdownOpen === item.name && (
                            <motion.ul
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                              className="mt-2 flex w-full flex-col gap-0.5 overflow-hidden"
                            >
                              {item.children.map((child) => (
                                <li key={child.name}>
                                  <Link
                                    href={child.href}
                                    className="block py-1.5 text-sm font-light uppercase tracking-wide text-accent/90 transition-colors duration-300 hover:text-accent"
                                    onClick={toggleMenu}
                                  >
                                    {child.name}
                                  </Link>
                                </li>
                              ))}
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        href={item.href}
                        className="block text-base font-light uppercase tracking-wide transition-colors duration-300 text-accent"
                        onClick={(e) => {
                          if (window.location.pathname === item.href) {
                            e.preventDefault();
                          }
                          toggleMenu();
                        }}
                      >
                        {item.name}
                      </Link>
                    )}
                  </motion.div>
                ))}
              </motion.div>
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  delay: mobileCtaDelay,
                  duration: 1.2,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <PrimaryButton variant="tenup-mobile" className="mt-8" />
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
